import React from "react";
import Select from "react-select";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import OrderFilter from "./OrderFilter";
import MaterialFilter from "./MaterialFilter";
import LineFilter from "@/modules/pg/shared/planningTable/lineOrderList/LineFilter";
import RescheduleOperationFilter from "./RescheduleOperationFilter";
import RescheduleLineFilter from "./RescheduleLineFilter";
import InsightsTypeFilter from "./InsightsTypeFilter";
import { TypeFilterValues } from "@/modules/shared/planningTable/typeFilterConsts";
import { SET_SELECTED_FILTER_TYPE, RESET_FILTERS } from "@/modules/pg/shared/planningTable/reducers/filters/filters";
import CustomFilter, { parseFilterConfigs } from "@/modules/pg/shared/planningTable/lineOrderList/filters/CustomFilter";
import { getBackend } from "@/lib/backend/backend2";
import { getPlantsForUser } from "@/lib/businessLogic";
import local_settings, { FIELD_LAST_PLANT } from "@/lib/localSettings";
import ReworkOrderFilter from "./ReworkOrderFilter";
import { SET_LOCK_FILTER } from "@/modules/pg/shared/planningTable/reducers/common";
import PlanningResultFilter from "./PlanningResultFilter";

import type { SetLockFilter } from "@/modules/pg/shared/planningTable/reducers/common";
import type {
    SetSelectedFilterType,
    ResetFiltersAction,
} from "@/modules/pg/shared/planningTable/reducers/filters/filters";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { SELECTED_FILTER_TYPES } from "@/modules/pg/shared/planningTable/reducers/filters/filters";
import type { CustomFilterConfig } from "@/modules/pg/shared/planningTable/lineOrderList/filters/CustomFilter";

type SelectOptionItem = {
    label: string;
    value: SELECTED_FILTER_TYPES | number;
};

type ActionTypes = SetSelectedFilterType | ResetFiltersAction | SetLockFilter;

type OwnProps = {};

type Props = OwnProps & ReduxProps;

type State = {
    custom_filter_configs: CustomFilterConfig[];
    selected_custom_filter: number;
    is_loading: boolean;
};

class TypeFilter extends React.Component<Props, State> {
    state: State = {
        custom_filter_configs: [],
        selected_custom_filter: -1,
        is_loading: false,
    };

    async componentDidMount() {
        await this.loadFilterSettings();
    }

    componentDidUpdate(prev_props: Props) {
        if (
            prev_props.selected_filter_type === TypeFilterValues.custom &&
            this.props.selected_filter_type !== TypeFilterValues.custom
        ) {
            this.setState({ selected_custom_filter: -1 });
        }
    }

    loadFilterSettings = async () => {
        const settings_file = "planning_table_filters.json";
        this.setState({
            is_loading: true,
        });

        try {
            const { content } = await getBackend().common.getBlobStorageItemAsStringOrNull({
                key: settings_file,
            });
            if (content !== null) {
                const plant_uuid = local_settings.getItem(FIELD_LAST_PLANT);
                const plant = getPlantsForUser().find(p => p.uuid === plant_uuid);
                const custom_filter_configs = parseFilterConfigs(content, plant && plant.external_id);
                this.setState({ custom_filter_configs });
            }
        } catch (error) {
            console.error(`${settings_file}:`, error);
        }

        this.setState({
            is_loading: false,
        });
    };

    getTypeOptions = (): SelectOptionItem[] => {
        const options: SelectOptionItem[] = [
            {
                value: TypeFilterValues.reschedule_line,
                label: translate("common.reschedule_on_line", "Reschedule on line"),
            },
            {
                value: TypeFilterValues.reschedule_operation,
                label: translate("common.reschedule_operation", "Reschedule operation"),
            },
            {
                value: TypeFilterValues.material,
                label: translate("common.materials", "Materials"),
            },
            {
                value: TypeFilterValues.line,
                label: translate("common.lines", "Lines"),
            },
            {
                value: TypeFilterValues.order,
                label: translate("common.order", "Order"),
            },
            {
                value: TypeFilterValues.order_insight_type,
                label: translate("common.insight_types", "Insight types"),
            },
            {
                value: TypeFilterValues.filter_rework_orders,
                label: translate("common.rework_orders", "Rework orders"),
            },
        ];

        for (let i = 0; i < this.state.custom_filter_configs.length; i++) {
            const settings = this.state.custom_filter_configs[i];
            options.push({
                value: i,
                label: settings.title,
            });
        }

        if (this.props.recommend_finished) {
            options.push({
                value: TypeFilterValues.planning_result_filter,
                label: translate("common.planning_result_filter", "Planning result filter"),
            });
        }

        return options;
    };

    onChangeFilterType = (option?: SelectOptionItem) => {
        if (!option) {
            this.props.reduxDispatch({ type: RESET_FILTERS, data: undefined });
            return;
        }

        const { value } = option;
        if (value === TypeFilterValues.filter_rework_orders) {
            this.props.reduxDispatch({ type: SET_LOCK_FILTER, data: true });
        }

        const filter_type = typeof value === "number" ? TypeFilterValues.custom : value;
        this.props.reduxDispatch({ type: SET_SELECTED_FILTER_TYPE, data: filter_type });

        const selected_custom_filter = typeof value === "number" ? value : -1;
        this.setState({ selected_custom_filter });
    };

    renderType = () => {
        const value = this.getTypeOptions().find(
            option =>
                option.value === this.props.selected_filter_type || option.value === this.state.selected_custom_filter
        );

        return (
            <Select
                value={value || null}
                placeholder={translate("common.select_filter_type", "Select type")}
                options={this.getTypeOptions()}
                onChange={this.onChangeFilterType}
                isSearchable={true}
                isLoading={this.state.is_loading}
                isClearable
                menuPortalTarget={document.body}
                styles={{
                    menuPortal: base => ({ ...base, zIndex: 100 }),
                    menu: base => ({ ...base, width: "max-content", minWidth: "100%" }),
                }}
            />
        );
    };

    renderOrdersFilter = () => {
        return <OrderFilter />;
    };

    renderMaterialsFilters = () => {
        return <MaterialFilter />;
    };

    renderLineFilter = () => {
        return <LineFilter />;
    };

    renderRescheduleOperationFilter = () => {
        return <RescheduleOperationFilter />;
    };

    renderRescheduleLineFilter = () => {
        return <RescheduleLineFilter />;
    };

    renderOrderInsightTypeFilter = () => {
        return <InsightsTypeFilter />;
    };

    renderReworkOrdersFilter = () => {
        return <ReworkOrderFilter />;
    };

    renderPlanningResultFilter = () => {
        return <PlanningResultFilter />;
    };

    renderCustomFilter = () => {
        const filter_config = this.state.custom_filter_configs[this.state.selected_custom_filter];
        if (filter_config === undefined) {
            return <Select isDisabled />;
        }

        const { entity, field, input_type, match, placeholder } = filter_config;
        return (
            <CustomFilter
                entity={entity}
                field={field[0] ?? ""}
                input_type={input_type}
                match={match}
                palceholder={placeholder}
            />
        );
    };

    renderValueOptions = () => {
        if (this.props.selected_filter_type === TypeFilterValues.line) {
            return this.renderLineFilter();
        }
        if (this.props.selected_filter_type === TypeFilterValues.order) {
            return this.renderOrdersFilter();
        }
        if (this.props.selected_filter_type === TypeFilterValues.material) {
            return this.renderMaterialsFilters();
        }
        if (this.props.selected_filter_type === TypeFilterValues.reschedule_operation) {
            return this.renderRescheduleOperationFilter();
        }
        if (this.props.selected_filter_type === TypeFilterValues.reschedule_line) {
            return this.renderRescheduleLineFilter();
        }
        if (this.props.selected_filter_type === TypeFilterValues.order_insight_type) {
            return this.renderOrderInsightTypeFilter();
        }
        if (this.props.selected_filter_type === TypeFilterValues.filter_rework_orders) {
            return this.renderReworkOrdersFilter();
        }
        if (this.props.selected_filter_type === TypeFilterValues.planning_result_filter) {
            return this.renderPlanningResultFilter();
        }
        if (this.props.selected_filter_type === TypeFilterValues.custom) {
            return this.renderCustomFilter();
        }

        return <Select isDisabled />;
    };

    render() {
        return (
            <div className="ql-type-filter">
                <div className="type-dropdown">{this.renderType()}</div>
                <div className="value-dropdown">{this.renderValueOptions()}</div>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const selected_filter_type = state.gantt_chart_filters.selected_filter_type;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const selected_line_uuids = state.gantt_chart_filters.selected_line_uuids;
    const order_external_ids = state.gantt_chart_filters.order_external_ids;
    const selected_stock_requirement = state.gantt_chart_stock_requirements_filter.selected_stock_requirement;
    const recommend_finished = state.gantt_chart_planning_table.recommend_plan.line_plans.length > 0;

    return {
        selected_filter_type,
        lines_orders,
        selected_line_uuids,
        order_external_ids,
        selected_stock_requirement,
        recommend_finished,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(TypeFilter);
